import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { DEFAULT_COMPANY } from '../../constants/company';
import { machineLabel } from '../../utils/machineLabel';
import { statusOf, groupByType } from '../../utils/floorStatus';
import { getAlarmCatalog, injectAlarm, clearAlarm, requestToolChange, finishToolChange, finishSetup } from '../../services/alarmApi';
import { useServerClock, SKEW_WARN_MS, describeSkew } from '../../utils/serverClock';

// ── Live Floor (CONTRACT §2) ─────────────────────────────────────────────────
// The single read-only mirror of the running plant. Absorbed the old Dashboard, which was a second
// view of the same floor built on a second source of truth; keeping both cost us two bugs:
//
//   1. The Dashboard filtered machines through a hardcoded type list and SILENTLY DROPPED anything
//      absent from it — so SAND_CORE and SAND_MOLD, the operations feeding the whole line, never
//      appeared (8 of 10 machines on Rassini). Grouping here degrades to a trailing "other" group,
//      never to omission. See `typeIndex`.
//   2. Status was derived twice, from two payloads — `.state` (the FOCAS-facing projection) here and
//      `running.phase` (engine truth) there — so one machine could read ACTIVE on one page and HELD
//      on the other. There is now exactly ONE derivation (`statusOf`) and the header counters are
//      computed from its output, so a chip cannot disagree with the badge it is counting.
//
// Data strategy is the old Dashboard's, which was the better half of it: `/machines` is the ROSTER
// SPINE and `/floor/live` an overlay joined onto it. `/floor/live` 404s when no engine is registered
// for a company, so overlay-only meant a bare error page for a fleet that demonstrably exists; this
// way the machines still list, unpopulated. The join key is the RAW machine name present in both
// payloads — not `displayName`, which only one of them sends.

const API_BASE = '/machinesim/api';
const SOCKET_PATH = '/machinesim/socket.io';

const FOCAS_STALE_MS = 15000;

const exportLinkStyle = {
  fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', color: 'var(--cn-gold)',
  textDecoration: 'none', padding: '4px 9px', borderRadius: 6,
  border: '1px solid rgba(224,168,64,0.3)', whiteSpace: 'nowrap',
};

const isFresh = (ts, windowMs, now) => {
  if (!ts) return false;
  const t = new Date(ts).getTime();
  return Number.isFinite(t) && now - t <= windowMs;
};

// Index the LiveMachine[] overlay by its raw `machine` name for an O(1) join onto the roster.
function indexLive(payload) {
  const out = {};
  for (const lm of Array.isArray(payload) ? payload : []) if (lm?.machine) out[lm.machine] = lm;
  return out;
}

export default function LiveFloorPage() {
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;

  const [machines, setMachines] = useState([]); // roster from /machines
  const [liveByName, setLiveByName] = useState({}); // overlay from /floor/live
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  // One shared 1s clock so countdowns tick smoothly and staleness stays honest between socket
  // pushes — anchored to SERVER time, not this device's. `phaseEndsAt` is issued by the server, so a
  // device clock that is off makes every deadline read wrong; a fast one clamps every countdown to
  // 0:00. See utils/serverClock.js — this page and Floor Control share the one implementation.
  const { now, skewMs, observe } = useServerClock();

  // REST hydrate + slow poll fallback (in case the socket is down). A missing engine costs us the
  // overlay, not the page: the roster call is what decides whether we render machines at all.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const fetchAll = async () => {
      try {
        const sentAt = Date.now();
        const [mRes, lRes] = await Promise.all([
          axios.get(`${API_BASE}/machines?company=${companySlug}`),
          axios.get(`${API_BASE}/floor/live?company=${companySlug}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        observe(mRes, sentAt);
        setMachines(mRes.data.machines || []);
        setLiveByName(indexLive(lRes.data));
        setError(null);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load floor');
        setLoading(false);
      }
    };
    fetchAll();
    const id = setInterval(fetchAll, 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [companySlug, observe]);

  // Socket — scoped to the company being viewed TWO ways: `joinCompany` puts us in the right room
  // (re-sent on every reconnect, since a reconnect is a fresh socket server-side) and the payload's
  // own slug is checked before it is applied. Belt and braces on purpose — `floorLiveUpdate` carries
  // the whole array, so one unscoped payload used to swap every card on screen for another tenant's.
  useEffect(() => {
    const socket = io({ path: SOCKET_PATH, transports: ['websocket', 'polling'], reconnection: true });
    socketRef.current = socket;
    const join = () => socket.emit('joinCompany', companySlug);
    socket.on('connect', () => {
      setIsConnected(true);
      join();
    });
    socket.on('disconnect', () => setIsConnected(false));
    socket.on('floorLiveUpdate', (payload, company) => {
      if (company && company !== companySlug) return;
      if (Array.isArray(payload)) setLiveByName(indexLive(payload));
    });
    socket.on('machineUpdate', (data) => {
      setMachines((prev) => {
        const idx = prev.findIndex((m) => m.name === data.name);
        if (idx < 0) return prev; // not on this roster → different company, ignore
        const next = [...prev];
        next[idx] = { ...prev[idx], state: data.state };
        return next;
      });
    });
    if (socket.connected) join();
    return () => socket.disconnect();
  }, [companySlug]);

  // Join overlay onto roster. Every roster machine appears exactly once, with or without an overlay.
  const rows = machines.map((m) => {
    const live = liveByName[m.name] || null;
    return {
      name: m.name,
      label: machineLabel(m.name, companySlug, m.machineType),
      type: String(m.machineType || live?.type || '').toUpperCase(),
      state: m.state || {},
      live,
      status: statusOf(live, m.state),
    };
  });

  // Counters read off the SAME derivation as the badges, so they cannot disagree with what's drawn.
  const countOf = (label) => rows.filter((r) => r.status.label === label).length;
  const runningCount = countOf('RUNNING');
  const setupCount = countOf('SETUP');
  const idleCount = countOf('IDLE');
  const tcCount = countOf('TOOL CHANGE');
  const starvedCount = countOf('STARVED');
  const driftCount = rows.filter((r) => r.live?.drift).length;
  const anyWork = rows.some((r) => r.live?.dispatched || r.live?.running || r.state?.work_order_id);

  // Flat grid, process order. Grouping into per-type sections read beautifully but cost ~40% of the
  // page height on this fleet shape: SAND_CORE and SAND_MOLD have one machine each, so each section
  // burned two thirds of a row on whitespace. Flattening the groups keeps the upstream-to-downstream
  // reading order (and the totality guarantee that every machine appears exactly once) while letting
  // cards flow continuously. The type legend above carries what the section headers used to.
  const groups = groupByType(rows);
  const ordered = groups.flatMap((g) => g.machines.map((m) => ({ ...m, color: g.color, typeLabel: g.label })));

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <div style={{ fontFamily: 'var(--cn-mono, monospace)', fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'var(--cn-gold)', marginBottom: 5 }}>
            {companySlug} · live
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--cn-text)', margin: 0 }}>Live Floor</h1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: isConnected ? 'var(--cn-success)' : 'var(--cn-muted)' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', ...(isConnected ? { animation: 'pulse 2s infinite' } : {}) }} />
              {isConnected ? 'Live' : 'Polling'}
            </div>
            {/* Far beyond round-trip noise — this browser's clock is set wrong. Countdowns are already
                corrected to server time; saying so keeps a wrong-looking floor from being blamed on
                the engine. */}
            {Math.abs(skewMs) > SKEW_WARN_MS && (
              <span title="Countdowns are corrected to server time. This device's clock is wrong."
                style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--cn-warning)', border: '1px solid var(--cn-warning)', borderRadius: 999, padding: '2px 8px' }}>
                ⏱ clock {describeSkew(skewMs)}
              </span>
            )}
          </div>
          {/* Export the machine-readable snapshot / schedule of the running floor. Plain anchors: the
              server sets Content-Disposition so the browser saves a timestamped .json. `download`
              hints the same. Both are company-scoped; the JSON is the inspection surface you can hand
              to another tool instead of reading the floor off this page. */}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* The touch/tablet twin of this page. Live Floor reads the floor; Control drives it —
                same data and the same `statusOf` vocabulary, laid out for a finger. */}
            <Link
              to={companySlug !== DEFAULT_COMPANY ? { pathname: '/control', search: `?company=${companySlug}` } : '/control'}
              style={{ ...exportLinkStyle, color: 'var(--cn-text)', borderColor: 'rgba(168,144,96,0.3)' }}
            >
              ▦ Tablet controls
            </Link>
            <a href={`${API_BASE}/simulation/snapshot?company=${companySlug}&download=1`} download style={exportLinkStyle}>
              ↓ Snapshot JSON
            </a>
            <a href={`${API_BASE}/simulation/schedule?company=${companySlug}&download=1`} download style={exportLinkStyle}>
              ↓ Schedule JSON
            </a>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 26 }}>
        <StatChip label="Running" value={runningCount} color="var(--cn-success)" />
        <StatChip label="Setup" value={setupCount} color={setupCount > 0 ? 'var(--cn-gold)' : 'var(--cn-muted)'} />
        <StatChip label="Starved" value={starvedCount} color={starvedCount > 0 ? 'var(--cn-warning)' : 'var(--cn-muted)'} />
        <StatChip label="Idle" value={idleCount} color="var(--cn-dimmed)" />
        <StatChip label="Tool change" value={tcCount} color={tcCount > 0 ? 'var(--cn-warning)' : 'var(--cn-muted)'} />
        <StatChip label="Drift" value={driftCount} color={driftCount > 0 ? 'var(--cn-danger)' : 'var(--cn-muted)'} />
      </div>

      {error && (
        <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)', borderRadius: 8, padding: '12px 15px', color: '#e8a89f', fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div style={{ color: 'var(--cn-muted)', padding: '48px 0', textAlign: 'center' }}>Loading floor…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: 'var(--cn-muted)', padding: '48px 0', textAlign: 'center' }}>
          No machines configured for “{companySlug}”.
        </div>
      ) : (
        <>
          {!anyWork && (
            <div style={{ color: 'var(--cn-muted)', fontSize: 13, marginBottom: 20, padding: '11px 14px', background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 8 }}>
              Nothing dispatched — every machine is idle.{' '}
              <Link
                to={companySlug !== 'default' ? { pathname: '/plan/sandbox', search: `?company=${companySlug}` } : '/plan/sandbox'}
                style={{ color: 'var(--cn-gold)', fontWeight: 600 }}
              >
                Dispatch a scenario from the Capacity Sandbox →
              </Link>
            </div>
          )}

          {/* Legend: the type vocabulary the flat grid no longer spells out per section. Ordered the
              same way the cards are, so it doubles as a map of the process. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid rgba(168,144,96,0.1)' }}>
            {groups.map((g) => (
              <div key={g.type} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: g.color }} />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--cn-muted)' }}>{g.label}</span>
                <span style={{ fontSize: 10, color: 'var(--cn-dimmed)' }}>{g.machines.length}</span>
              </div>
            ))}
          </div>

          {/* alignItems:start — grid rows otherwise stretch every card to the tallest in the row, so a
              starved machine with no cycle bar grew a block of dead space to match a running
              neighbour's tool table. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 18, alignItems: 'start' }}>
            {ordered.map((row) => (
              <MachineCard key={row.name} row={row} color={row.color} now={now} company={companySlug} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Countdown formatter for timed phases ("N:SS left").
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

function MachineCard({ row, color, now, company }) {
  const { name, label, typeLabel: typeName, state, live, status } = row;
  // Route and tooltip carry the RAW name: it is the machine's identity on the wire and in the DB,
  // and the prefix is how a cross-tenant leak gets spotted. Only the visible text is prettified.
  const detailTo = company && company !== 'default' ? { pathname: `/machine/${name}`, search: `?company=${company}` } : `/machine/${name}`;
  const r = live?.running;
  const partCount = r?.partCount ?? state.total_part_count ?? 0;
  const targetQty = r?.targetQty ?? live?.dispatched?.targetQty ?? state.target_qty ?? 0;
  const partPct = targetQty > 0 ? (partCount / targetQty) * 100 : 0;
  const cycleProgress = r?.cycleProgress ?? state.cycle_progress ?? 0;
  const showCycle = status.label === 'RUNNING';
  const wo = r?.workOrderId || live?.dispatched?.workOrderId || state.work_order_id || null;
  const program = r?.program || live?.dispatched?.program || null;
  // VCNC execution pointer — the block/line actually executing (LATHE/MILL with an authored program).
  // Null on foundry / un-authored ops, same as toolId. `execBlock` is the block source text, `execLine`
  // the source line, `execSeq` the N-word. These are the wire's `block`/`line` — the same data MM reads
  // off the FOCAS adapter, shown here from engine truth.
  const execBlock = r?.block ?? null;
  const execLine = r?.line ?? null;
  const execSeq = r?.sequence ?? null;
  // Transfer batching / starting inventory for the current op — the WIP transfer-lot size and any
  // castings seeded on hand. Previously these lived only in the schedule JSON; surface them here.
  const transferBatch = live?.dispatched?.transferBatchSize ?? 0;
  const seedWip = live?.dispatched?.seedWip ?? 0;
  // SETUP countdown: setup is a timed phase, so show "N:SS left" instead of a frozen-looking 0%.
  const isSetup = status.label === 'SETUP';
  const setupEndsMs = r?.phaseEndsAt ? new Date(r.phaseEndsAt).getTime() : null;
  const setupLeftSec = (isSetup && setupEndsMs) ? Math.max(0, Math.round((setupEndsMs - now) / 1000)) : null;
  // A CNC alarm that isn't the normal upstream-starve hold — worth showing even while idle.
  const alarm = (r?.alarmCode || state?.alarm_code) || null;
  const showAlarm = alarm && alarm !== 'UPSTREAM_CONSTRAINT' && status.label !== 'STARVED';
  const alarmMessage = r?.alarmMessage || state?.alarm_message || null;
  const alarmIsFault = !!(r?.faulted || state?.faulted) || (r?.alarmLevel || state?.alarm_level) === 'FAULT';

  return (
    <div style={{ background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.12)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ height: 3, background: color }} />
      <div style={{ padding: '14px 16px' }}>
        {/* Header: label + status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <Link
              to={detailTo}
              title={name}
              style={{ fontWeight: 700, fontSize: 15, color: 'var(--cn-text)', textDecoration: 'none' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--cn-gold)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--cn-text)')}
            >
              {label}
            </Link>
            {/* Type stays on the card even with a legend overhead. The 3px colour bar is the only
                other type channel, and colour alone is a poor one — unreadable to a colour-blind
                operator and already fighting this theme's open contrast debt. Renders the human
                label (`Sand Core`), not the raw enum the old card showed. */}
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--cn-dimmed)', textTransform: 'uppercase' }}>{typeName}</span>
          </div>
          <Badge color={status.color} filled>
            {status.label}
          </Badge>
        </div>

        {/* Streaming + queue + drift chips. Only shown once the overlay exists — an absent engine
            should read as "unknown", not as "off". */}
        {live && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            <StreamBadge
              kind="FOCAS"
              ok={live.streaming?.focas?.enabled && live.streaming?.focas?.bound && isFresh(live.streaming.focas.lastPollAt, FOCAS_STALE_MS, now)}
              enabled={!!(live.streaming?.focas?.enabled && live.streaming?.focas?.bound)}
              // Show the served port whenever focas-hero knows it — the number you enter in MM when
              // adding this machine's adapter, whether or not an adapter is polling it yet.
              suffix={live.streaming?.focas?.port ? `:${live.streaming.focas.port}` : null}
              title={
                live.streaming?.focas?.port
                  ? `${live.streaming.focas.host || 'focas-hero'} port ${live.streaming.focas.port}${live.streaming.focas.lastPollAt ? ` · last poll ${new Date(live.streaming.focas.lastPollAt).toLocaleTimeString()}` : ' · no adapter polling yet'}`
                  : 'no FOCAS port assigned'
              }
            />
            <span style={{ flex: 1 }} />
            <Chip title="Queued RELEASED/PLANNED ops for this machine">Q {live.queueDepth ?? 0}</Chip>
            {live.drift && <Badge color="var(--cn-danger)">⚠ DRIFT</Badge>}
          </div>
        )}

        {/* Tool-change banner (present only during a hold) */}
        {live?.toolChange && <ToolChangeBanner tc={live.toolChange} now={now} name={name} company={company} />}

        {/* Work order + program */}
        {wo ? (
          <div style={{ marginBottom: 10, fontSize: 12 }}>
            <div style={{ color: 'var(--cn-gold)', fontWeight: 600 }}>{wo}</div>
            <div style={{ color: 'var(--cn-muted)', fontSize: 11, marginTop: 2 }}>
              {program ? `Program ${program}` : 'No program'}
              {live?.dispatched?.status && <span style={{ marginLeft: 6, color: 'var(--cn-dimmed)' }}>· {live.dispatched.status.replace(/_/g, ' ')}</span>}
            </div>
            {state.program_header && <div style={{ color: 'var(--cn-dimmed)', fontSize: 11, marginTop: 2 }}>{state.program_header}</div>}
            {/* WIP transfer lot for this op + any castings seeded on hand. */}
            {transferBatch > 0 && (
              <div style={{ color: 'var(--cn-dimmed)', fontSize: 11, marginTop: 2 }}>
                Transfer lot {transferBatch}
                {seedWip > 0 && <span> · {seedWip} on hand</span>}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: 'var(--cn-dimmed)', fontSize: 12, marginBottom: 10 }}>No work order queued</div>
        )}

        {/* SETUP is a timed phase, not production — say so, with the countdown, so it doesn't read as a
            frozen RUNNING at 0 parts / 0% cycle. */}
        {isSetup && <SetupRow leftSec={setupLeftSec} program={program} name={name} company={company} />}

        {/* A live CNC alarm (not the normal upstream-starve hold) — visible even while idle. */}
        {showAlarm && (
          <div style={{ fontSize: 11, color: alarmIsFault ? 'var(--cn-danger)' : 'var(--cn-warning)', marginBottom: 8, fontWeight: 600 }}>
            {alarmIsFault ? '⛔' : '⚠'} {String(alarm).replace(/_/g, ' ')}
            {alarmMessage && <span style={{ fontWeight: 500, color: 'var(--cn-muted)' }}> — {alarmMessage}</span>}
          </div>
        )}

        {/* Part progress */}
        {targetQty > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--cn-muted)', marginBottom: 4 }}>
              <span>Parts</span>
              <span style={{ fontFamily: '"Courier New", monospace' }}>
                {partCount} / {targetQty}
              </span>
            </div>
            <Bar pct={partPct} color={color} />
          </div>
        )}

        {/* Cycle progress (only while actually producing) */}
        {showCycle && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--cn-muted)', marginBottom: 4 }}>
              <span>Cycle</span>
              <span style={{ fontFamily: '"Courier New", monospace' }}>{Math.round(cycleProgress)}%</span>
            </div>
            <Bar pct={cycleProgress} color="var(--cn-success)" />
            {/* Executing block/line — the same block & line data items MM reads off the FOCAS adapter,
                shown here from engine truth. Present only while the VCNC is driving this machine. */}
            {(execBlock || execLine != null) && (
              <div
                title={`block: ${execBlock || '—'}${execSeq != null ? ` · N${execSeq}` : ''}${execLine != null ? ` · line ${execLine}` : ''}`}
                style={{ display: 'flex', gap: 8, marginTop: 5, fontSize: 11, fontFamily: '"Courier New", monospace', minWidth: 0 }}
              >
                {execLine != null && <span style={{ color: 'var(--cn-dimmed)', flexShrink: 0 }}>L{execLine}</span>}
                {execBlock && (
                  <span style={{ color: 'var(--cn-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {execSeq != null ? `N${execSeq} ` : ''}{execBlock}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {status.label === 'STARVED' && <div style={{ fontSize: 11, color: 'var(--cn-warning)', marginTop: 8 }}>Waiting for upstream parts</div>}

        {/* Tool wear counters. An untouched turret collapses to one line: four bars reading
            `0 / 50 (0%)` gave an idle machine the tallest card on the floor while saying nothing.
            The tools are still named, so "this machine has tooling configured" survives. */}
        {Array.isArray(live?.tools) && live.tools.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(168,144,96,0.1)' }}>
            <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--cn-dimmed)', marginBottom: 8 }}>Tools</div>
            {live.tools.every((t) => !t.count) ? (
              <div style={{ fontSize: 11, color: 'var(--cn-dimmed)' }}>
                {live.tools.map((t) => t.key).join(' · ')} — unused
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {live.tools.map((t) => (
                  <ToolRow key={t.key} t={t} name={name} company={company} changing={live.toolChange?.toolsChanging?.includes(t.key)} />
                ))}
              </div>
            )}
          </div>
        )}
        <AlarmControl name={name} company={company} faulted={!!r?.faulted} alarmPresent={!!showAlarm} />
      </div>
    </div>
  );
}

// Inject / clear a CNC alarm on this machine. Picker seeded from the real fleet catalog, plus a Custom
// option (any code/message/level). FAULT halts + shows in MM; WARNING is message-only. When the machine
// is faulted, the primary action is Clear.
function AlarmControl({ name, company, faulted, alarmPresent }) {
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState('');
  const [custom, setCustom] = useState({ code: '', message: '', level: 'FAULT' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { getAlarmCatalog().then(setCatalog).catch(() => {}); }, []);

  const run = async (fn) => { setBusy(true); setErr(null); try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const doInject = () => run(async () => {
    const a = sel === 'custom'
      ? { code: custom.code.trim(), message: custom.message.trim(), level: custom.level }
      : (() => { const c = catalog[Number(sel)]; return c ? { code: c.code, message: c.message, level: c.level } : null; })();
    if (!a || (!a.code && !a.message)) { setErr('pick or enter an alarm'); return; }
    await injectAlarm(name, a, company); setOpen(false);
  });
  const doClear = () => run(() => clearAlarm(name, company));

  const btn = { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid var(--cn-border, rgba(168,144,96,0.3))', background: 'transparent', color: 'var(--cn-muted)' };
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid rgba(168,144,96,0.08)', paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Shown for ANY live alarm, not just a halting one. A WARNING (2015 DOOR OPEN) leaves
            `faulted` false while the alarm is live on the wire and visible in MachineMetrics, and
            gating Clear on `faulted` left the operator looking at an alarm with no way to clear it. */}
        {(faulted || alarmPresent) && (
          <button style={{ ...btn, color: 'var(--cn-success)', borderColor: 'var(--cn-success)' }} disabled={busy} onClick={doClear}>
            {busy ? '…' : '✓ Clear alarm'}
          </button>
        )}
        <button style={btn} disabled={busy} onClick={() => setOpen((v) => !v)}>⚠ Inject alarm</button>
      </div>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <select value={sel} onChange={(e) => setSel(e.target.value)} style={fpAlarmInput}>
            <option value="">Select an alarm…</option>
            {catalog.map((a, i) => <option key={i} value={i}>{a.level === 'FAULT' ? '⛔' : '⚠'} {a.code} — {a.message}</option>)}
            <option value="custom">Custom…</option>
          </select>
          {sel === 'custom' && (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <input placeholder="code (e.g. SV0401)" value={custom.code} onChange={(e) => setCustom({ ...custom, code: e.target.value })} style={{ ...fpAlarmInput, flex: 1 }} />
                <select value={custom.level} onChange={(e) => setCustom({ ...custom, level: e.target.value })} style={{ ...fpAlarmInput, width: 90 }}>
                  <option>FAULT</option><option>WARNING</option>
                </select>
              </div>
              <input placeholder="message" value={custom.message} onChange={(e) => setCustom({ ...custom, message: e.target.value })} style={fpAlarmInput} />
            </>
          )}
          <button style={{ ...btn, color: 'var(--cn-danger)', borderColor: 'var(--cn-danger)', alignSelf: 'flex-start' }} disabled={busy} onClick={doInject}>
            {busy ? 'Injecting…' : 'Inject'}
          </button>
        </div>
      )}
      {err && <div style={{ fontSize: 10.5, color: 'var(--cn-danger)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}

const fpAlarmInput = { fontSize: 11.5, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(168,144,96,0.3)', background: 'var(--cn-surface)', color: 'var(--cn-text)' };

// SETUP is a timed phase, not production — say so, with the countdown, so it doesn't read as a frozen
// RUNNING at 0 parts / 0% cycle. FINISH NOW is the operator saying the changeover is already done: it
// ends the hold and starts cutting, the twin of the tool-change control below. Same reasoning — the
// timer models real changeover downtime, but a simulation run usually wants the next event.
function SetupRow({ leftSec, program, name, company }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  const doFinish = async () => {
    setBusy(true); setErr(null);
    try { await finishSetup(name, company); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--cn-gold)', fontWeight: 600 }}>
          Setting up{leftSec != null ? ` \u00b7 ${mmss(leftSec)} left` : ''}{program ? ` \u00b7 loading ${program}` : ''}
        </span>
        {name && (
          <button
            title="Changeover is done — start cutting now"
            onClick={doFinish}
            disabled={busy}
            style={{
              flexShrink: 0, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', padding: '4px 9px',
              borderRadius: 999, border: '1px solid var(--cn-gold)',
              background: 'transparent', color: 'var(--cn-gold)', opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? '\u2026' : 'FINISH NOW'}
          </button>
        )}
      </div>
      {err && <div style={{ fontSize: 10, color: 'var(--cn-danger)', marginTop: 2 }}>{err}</div>}
    </div>
  );
}

function ToolChangeBanner({ tc, now, name, company }) {
  const endMs = tc.endsAt ? new Date(tc.endsAt).getTime() : null;
  const remaining = Number.isFinite(endMs) ? Math.max(0, Math.round((endMs - now) / 1000)) : Math.max(0, tc.remainingSec ?? 0);
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  // "Finish now" is the operator saying the swap is already done — it resets the due tools and drops
  // the hold instead of running the downtime out. The timer is there to model real tool-change
  // downtime; during a simulation run you often want the next event, not the wait.
  const doFinish = async () => {
    setBusy(true); setErr(null);
    try { await finishToolChange(name, company); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(224,168,64,0.4)', background: 'rgba(224,168,64,0.1)' }}>
      <span style={{ fontSize: 16 }}>🔧</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cn-warning)', letterSpacing: '0.04em' }}>
          TOOL CHANGE · {(tc.toolsChanging || []).join(', ') || '—'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--cn-muted)' }}>
          resumes in{' '}
          <span style={{ fontFamily: '"Courier New", monospace', color: 'var(--cn-text)', fontWeight: 700 }}>
            {mm}:{ss}
          </span>
        </div>
        {err && <div style={{ fontSize: 10, color: 'var(--cn-danger)', marginTop: 2 }}>{err}</div>}
      </div>
      {name && (
        <button
          title="Tools are swapped — end the hold now and resume cutting"
          onClick={doFinish}
          disabled={busy}
          style={{
            flexShrink: 0, cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap',
            fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', padding: '4px 9px',
            borderRadius: 999, border: '1px solid var(--cn-warning)',
            background: 'transparent', color: 'var(--cn-warning)', opacity: busy ? 0.5 : 1,
          }}
        >
          {busy ? '…' : 'FINISH NOW'}
        </button>
      )}
    </div>
  );
}

function ToolRow({ t, name, company, changing }) {
  const pct = Math.max(0, Math.min(100, t.pct ?? 0));
  const col = pct >= 100 ? 'var(--cn-danger)' : pct >= 80 ? 'var(--cn-warning)' : 'var(--cn-success)';
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const requested = !!t.retireRequested && !changing;   // once it enters the hold it's "changing", not "queued"

  const doRequest = async () => {
    setBusy(true); setErr(null);
    try { await requestToolChange(name, t.key, company); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // A tool that is already mid-change (in the hold) or has a request queued shows status, not a button.
  // Otherwise a compact 🔧 lets the operator queue a next-boundary change for exactly this slot.
  const showBtn = name && !changing && !requested;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 11, marginBottom: 3 }}>
        <span style={{ color: changing ? 'var(--cn-warning)' : 'var(--cn-muted)', fontWeight: changing ? 700 : 500 }}>
          {changing && '↻ '}
          {t.key}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: '"Courier New", monospace', color: 'var(--cn-muted)' }}>
            {t.count}
            {t.preset ? ` / ${t.preset}` : ''} <span style={{ color: col }}>({pct}%)</span>
          </span>
          {requested && (
            <span title="Change queued — fires at the next completed part" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--cn-warning)', border: '1px solid var(--cn-warning)', borderRadius: 999, padding: '1px 6px', whiteSpace: 'nowrap' }}>
              ⏳ QUEUED
            </span>
          )}
          {showBtn && (
            <button
              title={`Request a tool change for ${t.key} (next part boundary)`}
              onClick={doRequest}
              disabled={busy}
              style={{ fontSize: 10, fontWeight: 700, lineHeight: 1, padding: '2px 6px', borderRadius: 6, cursor: busy ? 'default' : 'pointer', border: '1px solid rgba(168,144,96,0.3)', background: 'transparent', color: 'var(--cn-muted)' }}
            >
              {busy ? '…' : '🔧 change'}
            </button>
          )}
        </span>
      </div>
      <Bar pct={pct} color={col} />
      {err && <div style={{ fontSize: 9.5, color: 'var(--cn-danger)', marginTop: 2 }}>{err}</div>}
    </div>
  );
}

// ── Small building blocks ─────────────────────────────────────────────────────
function StatChip({ label, value, color }) {
  return (
    <div style={{ padding: '10px 20px', background: 'var(--cn-surface)', border: '1px solid rgba(168,144,96,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: 'var(--cn-muted)', marginTop: 2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      </div>
    </div>
  );
}

function Badge({ children, color, filled }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        padding: '3px 8px',
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
        background: filled ? 'var(--cn-surface2)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function Chip({ children, title }) {
  return (
    <span
      title={title}
      style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 6, color: 'var(--cn-muted)', background: 'var(--cn-surface2)', border: '1px solid rgba(168,144,96,0.16)', whiteSpace: 'nowrap' }}
    >
      {children}
    </span>
  );
}

// Green when actively streaming (fresh), dim outline when enabled-but-stale, dark when off.
// `suffix` (e.g. the FOCAS port ":8211") renders after the label in a lighter weight, and stays
// visible even when the badge is dim — the port is useful precisely when no adapter is polling yet.
function StreamBadge({ kind, ok, enabled, title, suffix }) {
  const color = ok ? 'var(--cn-success)' : enabled ? 'var(--cn-muted)' : 'var(--cn-dimmed)';
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        padding: '3px 8px',
        borderRadius: 6,
        color,
        border: `1px solid ${ok ? color : 'rgba(168,144,96,0.16)'}`,
        background: ok ? 'var(--cn-surface2)' : 'transparent',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, ...(ok ? { boxShadow: `0 0 6px ${color}` } : {}) }} />
      {kind}
      {suffix && <span style={{ fontWeight: 600, color: 'var(--cn-text)', letterSpacing: 0 }}>{suffix}</span>}
    </span>
  );
}

function Bar({ pct, color }) {
  return (
    <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
    </div>
  );
}
